import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { Row, Col, Card, Button, Badge, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";

export default function IssuedProductDetailPage() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [product, setProduct] = useState(location.state?.product || null);
    const [loading, setLoading] = useState(!location.state?.product);
    const [error, setError] = useState(null);

    // Fallback API Fetching if page directly accessed or refreshed
    const fetchProductById = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/issuer/products/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            const resData = response.data;
            if (resData.success || resData.data) {
                setProduct(resData.data || resData);
            } else {
                setError(resData.message || "Failed to fetch product details.");
            }
        } catch (err) {
            console.error("Error fetching product details:", err);
            setError(
                err.response?.data?.message || "Failed to load product details."
            );
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (!product && id) {
            fetchProductById();
        }
    }, [id, product, fetchProductById]);

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("loading_product_details...")}</p>
                </div>
            </PageLayout>
        );
    }

    if (error || !product) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Alert variant="danger" className="d-inline-block px-5">
                        {error || t("no product data available")}
                    </Alert>
                    <div>
                        <Button
                            variant="primary"
                            className="mt-3"
                            onClick={() => navigate(-1)}
                        >
                            {t("back to list")}
                        </Button>
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("issued product details")}
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link
                                            to="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(-1);
                                            }}
                                            className="mc-breadcrumb-link"
                                        >
                                            {t("issued products")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        {t("product details")}
                                    </li>
                                </ul>
                            </div>
                            <Button
                                variant="secondary"
                                onClick={() => navigate(-1)}
                            >
                                {t("back")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col lg={4}>
                    <Card className="mb-4">
                        <Card.Body className="text-center">
                            {product.first_img ? (
                                <img
                                    src={`${process.env.REACT_APP_IMG_URL}${product.first_img}`}
                                    alt={product.title}
                                    className="img-fluid rounded mb-3"
                                    style={{ maxHeight: "250px", objectFit: "cover" }}
                                />
                            ) : (
                                <div
                                    className="bg-light d-flex align-items-center justify-content-center rounded mb-3"
                                    style={{ height: "200px" }}
                                >
                                    <span className="text-muted">{t("no image")}</span>
                                </div>
                            )}
                            <h5>{product.title || "N/A"}</h5>
                            <Badge bg="primary" className="mb-2">
                                {product.product_code || "N/A"}
                            </Badge>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={8}>
                    <Card className="mb-4">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">{t("product information")}</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                <Col sm={6}>
                                    <strong>{t("category")}: </strong>
                                    <span>
                                        {typeof product.category === "object"
                                            ? product.category?.name
                                            : product.category || "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("brand")}: </strong>
                                    <span>
                                        {typeof product.brand === "object"
                                            ? product.brand?.name
                                            : product.brand || "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("lister")}: </strong>
                                    <span>
                                        {typeof product.lister === "object"
                                            ? product.lister?.name
                                            : product.lister_name || "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("store")}: </strong>
                                    <span>
                                        {typeof product.store === "object"
                                            ? product.store?.name
                                            : product.store || "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("market placer")}: </strong>
                                    <span>
                                        {typeof product.marketPlacer === "object"
                                            ? product.marketPlacer?.name
                                            : product.marketPlacer || "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("condition")}: </strong>
                                    <span>{product.product_condition || "N/A"}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("selling style")}: </strong>
                                    <span>{product.selling_style || "N/A"}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("quantity")}: </strong>
                                    <span>{product.product_qty ?? 0}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("short buying price")}: </strong>
                                    <span>
                                        {product.short_buying_price !== null && product.short_buying_price !== undefined
                                            ? `$${product.short_buying_price}`
                                            : "N/A"}
                                    </span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("Buying price")}: </strong>
                                    <span>${product.buying_price ?? product.price ?? 0}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("sell price dollar")}: </strong>
                                    <span>${product.sell_price_dollar ?? 0}</span>
                                </Col>

                                <Col sm={6}>
                                    <strong>{t("shipping charges dollar")}: </strong>
                                    <span>${product.shipping_charges_dollar ?? 0}</span>
                                </Col>

                                
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}